import * as THREE from 'three';
import { SCOTOPIC_ROD_W, SCOTOPIC_TINT_N, nightFactor } from './exposure';
import { SPACE_GLSL } from './farShell';
import { BODY_AIR_GLSL, bodyAirUniforms, setBodyAir } from './bodyAir';
import type { BodyAirUniforms } from './bodyAir';

// The Milky Way: the light of every star too faint to be a point in the catalog, drawn as a smooth
// glow over the night sky.
//
// The server sends one map, once (the `glow` message, see broadcast_server.py): flux per steradian
// in galactic longitude and latitude, from the same galaxy model that produced the catalog stars, so
// the band, the bulge, the arms and the dust lanes line up with the stars drawn in front of them.
// A direction in the sky is turned into galactic coordinates in the fragment shader from the world's
// own local frame (the same one the stars use) and the map's basis, so the band turns with the day,
// rises and sets, and shifts through the year exactly as the stars do.
//
// Brightness. The map holds flux per steradian in the light field's units. Surface brightness in
// the sky's own irradiance-equivalent units is pi times that (a uniform sky of radiance L is an
// irradiance of pi L), and from there it is treated like the stars and the moon: dimmed and reddened
// by the atmosphere along its own air mass, run through the sky's curve 1 - exp(-SKY_EXPOSURE * gain
// * E), and added to the sky in front of it with a screen blend. The same exposure gain applies, so a
// bright moon or a dusk sky washes the band out by the same physical ratio that hides faint stars.
// GALAXY_GAIN scales it relative to the stars: the band is a faint extended glow that a dark-adapted
// eye reads by contrast, and a screen has no way to show that without a little help.
//
// Colour. In the dark the cones give out and the rods take over, so a faint glow loses its hue. The
// band is pulled toward the cool rod grey as the night curve takes over (exposure.ts), but not all the
// way: the bright bulge stays warm, which is also what a long exposure shows.

export interface GlowMessage {
  type: 'glow';
  width: number;
  height: number;
  log2_min: number;
  log2_max: number;
  basis: number[];                  // 3x3 row-major: core, perpendicular, plane normal (universe frame)
  magnitude_limit: number;
  data: string;                     // base64 of zlib of three planes of width*height uint16
}

export interface GalaxyViewState {
  frame: ArrayLike<number>;         // 3x3 row-major: east, north, up in universe space (as for the stars)
}

// Tunables.
export const GALAXY_GAIN      = 32.0;      // brightness of the band against the stars (1 = the sky's own curve)
export const GALAXY_DARK_LO   = 3.0e4;     // the band is invisible below this exposure gain (a bright twilight sky, or a moonlit night: a full moon is about 7e3) ...
export const GALAXY_DARK_HI   = 1.5e5;     // ... and at full strength above this (a moonless night is 2e5), fading in between
export const GALAXY_SOFT      = 0.0;     // how much of the band is the wide, soft version of the map (0 = as sent, 1 = fully blurred)
export const GALAXY_SOFT_LOD  = 4.0;     // how far apart the blur's taps are, in map pixels (about 0.7 degrees each)
export const GALAXY_MAX       = 0.0075;  // most the band can add to the screen, as linear light before the output curve (0.0075 is about 21/255): it can be bright in the world and still faint on the screen
export const GALAXY_SURROUND  = 0.45;    // share of the median glow taken as background and subtracted
export const GALAXY_ARM_HUE   = [0.80, 0.88, 1.40] as const;   // colour of the band away from the core (relative, scaled to keep brightness): blue-white to violet, as the unresolved light of young stars reads by eye
export const GALAXY_CORE_HUE  = [1.00, 0.97, 0.92] as const;   // colour of the bulge: a warm cream (the map's own tint is a deeper orange than the eye sees)
export const GALAXY_HUE_PULL  = 0.90;    // how far the map's own colour is pulled to these (0 = as the map, 1 = fully these); the map's lanes and knots still tint on top
export const GALAXY_CORE_COS_LO = 0.85;  // the arm colour gives way to the bulge's between these cosines of the angle from the core (about 32 and 8 degrees)
export const GALAXY_CORE_COS_HI = 0.99;
export const GALAXY_SCOT_MAX  = 0.65;    // most the band's colour is pulled toward the rods' cool grey, at night
export const GALAXY_MIN_GAIN  = 300;     // below this exposure gain (daylight, dusk) the band is not drawn
export const STRUCT_TAU0      = 2.4;     // dust lanes: optical depth at the densest part of the rift (the baked structure, see bakeStructure)
export const STRUCT_CLOUD     = 1.2;     // star-cloud knots: how much brighter and dimmer than the smooth map they run
export const STRUCT_RIFT_DEG  = 4.0;     // half-width of the rift near the core, degrees
export const STRUCT_GAIN      = 3.8;     // brightness given back to the band for the light the dust takes, strongest on the rift and 1 away from it (the dust darkens the band's average)
export const NEB_LEVEL        = 0.0012;  // nebula tint, as screen light before the output curve (the ceiling is GALAXY_MAX)
export const GRAIN            = 0.3;     // fine star grain, a tenth of a degree and below (0 = none)
export const STRUCT_SEED      = 11.0;
const TONE_BASE_EXPOSURE      = 1.3;     // must match the renderer's BASE_EXPOSURE
const SKY_EXPOSURE            = 6.0;     // must match the renderer's skylight -> colour curve

function lumNorm(c: ArrayLike<number>): THREE.Vector3 {
  return new THREE.Vector3(c[0], c[1], c[2]).divideScalar(0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]);
}

const VERT = /* glsl */ `
varying vec3 vDir;
void main() {
  vDir = position;                                   // object space = sky axes (the mesh is never rotated)
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAG = /* glsl */ `
uniform sampler2D uGlow;
uniform mat3  uFrame;           // rows east, north, up: local = uFrame * universe_direction
uniform mat3  uBasis;           // rows core, perpendicular, normal: galactic = uBasis * universe_direction
uniform float uScale;           // map value to flux per steradian
uniform vec3  uSurround;        // the glow the eye takes as the background, flux per steradian per channel
uniform float uMax;
uniform float uSoft;
uniform float uSoftLod;
uniform vec2  uTexel;
uniform float uSurroundK;       // how much of it is subtracted
uniform float uGainScale;       // SKY_EXPOSURE * GALAXY_GAIN * display gain * pi
uniform float uScot;            // night factor 0..1
uniform float uScotMax;
uniform vec3  uArmHue, uCoreHue;  // the band's colour away from the core and in the bulge (luminance 1)
uniform float uHuePull;
uniform vec2  uCoreCos;         // cosines of the angle from the core: arm colour at the first, bulge colour at the second
uniform vec3  uRodW;
uniform vec3  uRodTint;
uniform float uSrgbOut;
uniform samplerCube uCloudT;    // the sky's probe: alpha is the share of starlight the cloud lets through
uniform float uCloudOn;
uniform samplerCube uS1;          // baked structure: rgb dust transmittance, a star-cloud gain
uniform samplerCube uS2;          // baked structure: faint nebula tint (flux relative)
uniform float uStructOn;
uniform float uNebLevel;        // flux per steradian of a nebula tint at full strength
uniform float uGrain;           // amplitude of the fine star grain
varying vec3  vDir;

const float PI = 3.14159265358979;
const float PI_ = 3.14159265358979;
${SPACE_GLSL}
${BODY_AIR_GLSL}

vec3 encodeSrgb(vec3 c) {
  return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
}
float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float hash13(vec3 p3) { p3 = fract(p3 * 0.1031); p3 += dot(p3, p3.zyx + 31.32); return fract((p3.x + p3.y) * p3.z); }
float vnoise(vec3 p) {
  vec3 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  float a = hash13(i), b = hash13(i + vec3(1,0,0)), c = hash13(i + vec3(0,1,0)), d = hash13(i + vec3(1,1,0));
  float e = hash13(i + vec3(0,0,1)), g = hash13(i + vec3(1,0,1)), h = hash13(i + vec3(0,1,1)), k = hash13(i + vec3(1,1,1));
  return mix(mix(mix(a, b, f.x), mix(c, d, f.x), f.y), mix(mix(e, g, f.x), mix(h, k, f.x), f.y), f.z);
}

void main() {
  vec3 t = normalize(vDir);                          // three's axes: x east, y up, z south
  vec3 loc = vec3(t.x, -t.z, t.y);                   // the local frame: east, north, up
  // The planet hides what is below its true horizon, the moon what it covers; the air in the way is the sky-view table's transmittance along this direction (bodyAir.ts, as for the stars)
  if (belowTrueHorizon(t) || occludedBy(uOcc, t) || (uSVOn < 0.5 && occludedBy(uHome, t))) discard;
  vec3 trans = bodyTrans(t);

  vec3 u = loc * uFrame;                             // universe direction (the transpose of the frame)
  vec3 g = uBasis * u;                               // galactic: x toward the core, y toward longitude 90, z to the pole
  float lon = atan(g.y, g.x);
  float lat = asin(clamp(g.z, -1.0, 1.0));
  vec2 uv = vec2(lon / (2.0 * PI) + 0.5, lat / PI + 0.5);
  // Diffuse: mostly the wide, soft version of the map (a high mip level, blended with a lower one so no blocks show),
  // with a little of the sharp map left in for the bulge and the clumps.
  vec3 Lw = vec3(0.0);
  if (uSoft > 0.001) for (int iy = -2; iy <= 2; iy++) for (int ix = -2; ix <= 2; ix++) {
    vec2 o = vec2(float(ix), float(iy)) * uSoftLod * uTexel;
    float w = exp(-0.35 * float(ix * ix + iy * iy));
    Lw += w * textureLod(uGlow, uv + o, 1.5).rgb;
  }
  if (uSoft > 0.001) Lw /= 8.42;                                         // the weights sum to this
  // atan wraps from +pi to -pi, so the screen derivatives of uv jump there and the mip choice with them (a hairline of the coarsest mip
  // across the sky): the derivatives are taken with the wrap removed.
  vec2 duvx = dFdx(uv), duvy = dFdy(uv);
  duvx.x -= floor(duvx.x + 0.5); duvy.x -= floor(duvy.x + 0.5);
  vec3 L = mix(textureGrad(uGlow, uv, duvx, duvy).rgb, Lw, uSoft) * uScale;   // flux per steradian
  vec3 stT = vec3(1.0), stNeb = vec3(0.0);
  if (uStructOn > 0.5) {
    vec4 s1 = textureCube(uS1, g);          // cube maps: no longitude seam, no pole pinch, and the hardware's mip choice is right everywhere
    vec3 neb = textureCube(uS2, g).rgb;
    // star grain: the unresolved stars are not smooth at a tenth of a degree; faded out where it is finer than a pixel
    float fwg = length(fwidth(g));
    float g1 = vnoise(g * 150.0) , g2 = vnoise(g * 480.0 + 7.0);
    float w1 = 1.0 - smoothstep(0.2, 0.65, fwg * 150.0), w2 = 1.0 - smoothstep(0.2, 0.65, fwg * 480.0);
    float grain = exp(uGrain * (w1 * (g1 - 0.5) + 0.6 * w2 * (g2 - 0.5)));
    stT = s1.rgb * s1.a * grain; stNeb = neb;
  }

  // Lateral inhibition: the retina answers to a glow against its surround, not to its absolute level,
  // so the broad background that fills the whole sky is taken out (with a soft knee) and the band, the
  // bulge and the lanes stand out of it. Dust lanes darker than the surround go to black.
  vec3 d = L - uSurroundK * uSurround;
  vec3 above = 0.5 * (d + sqrt(d * d + (0.12 * uSurround) * (0.12 * uSurround)));
  // Colour. The map's own colour is a physical mix of starlight and comes out a deep orange; to the eye the faint band is blue-white to
  // violet in the arms, with only the bulge warm. The brightness is kept, the hue is pulled toward those colours (before the air's
  // reddening, which stays).
  float la = dot(above, vec3(0.2126, 0.7152, 0.0722));
  vec3 hue = above / max(la, 1e-12);
  hue = mix(hue, mix(uArmHue, uCoreHue, smoothstep(uCoreCos.x, uCoreCos.y, g.x)), uHuePull);
  above = hue * la;
  vec3 e = uGainScale * above * trans * mix(1.0, textureLod(uCloudT, t, 0.0).a, uCloudOn);   // cloud in front hides the glow
  float k = uScot * uScotMax;
  e = mix(e, dot(e, uRodW) * uRodTint, k);
  vec3 s = 1.0 - exp(-e);                            // the sky's own curve
  // A soft ceiling, so the band stays faint whatever the exposure does. It acts on the brightness (luminance) and keeps the colour: a ceiling
  // applied to each channel on its own pulls every channel to the same level wherever the band is bright, and the band goes grey.
  float ls = dot(s, vec3(0.2126, 0.7152, 0.0722));
  s *= uMax * (1.0 - exp(-ls / uMax)) / max(ls, 1e-9);
  // The lanes, knots and tints are ratios against their surroundings, applied to what is on the screen (so the tone curve,
  // old or new, does not flatten them). A last soft ceiling keeps the knots from running past the ceiling.
  if (uStructOn > 0.5) {
    s = s * stT + stNeb * uNebLevel;
    float top = uMax * 1.25;
    ls = dot(s, vec3(0.2126, 0.7152, 0.0722));
    s *= top * (1.0 - exp(-ls / top)) / max(ls, 1e-9);
  }
  if (uSrgbOut > 0.5) s = encodeSrgb(s);
  // A little noise so a smooth, very dark gradient does not band in eight bits.
  s += (hash12(gl_FragCoord.xy) - 0.5) / 255.0;
  gl_FragColor = vec4(max(s, vec3(0.0)), 1.0);       // blended as a screen: dst + src * (1 - dst)
}
`;


// ---- Baked structure ------------------------------------------------------------------------------------------------
// The glow map is 0.7 degrees per texel and comes from a smooth density model, so it has no lanes, knots or filaments of its own.
// The galaxy as seen from inside is a fixed picture, so the structure is made once, on the GPU, in the same longitude/latitude
// frame as the map, from noise on the unit direction, in cube maps (no seam, no pole pinch), at 0.18 degrees per texel:
//   S1.rgb  transmittance of the dust (a little warm: the blue is taken a little more), S1.a  gain of star clouds (log-normal, around 1)
//   S2.rgb  faint nebula tint
const ST_N = 512;               // cube face size, texels (about 0.18 degrees at a face's centre)
const ST_VERT = `void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }`;
const ST_FRAG = /* glsl */`
precision highp float;
uniform float uPass;            // 0: dust and clouds, 1: nebulae
uniform int uFace;              // the cube face being drawn (GL order: +x -x +y -y +z -z)
uniform float uGain;            // STRUCT_GAIN
uniform float uTau0, uCloudAmp, uSeed, uRiftW, uWarp, uFine, uFineF, uRidgeW;
const float PI = 3.14159265358979;
float hash13(vec3 p3) { p3 = fract(p3 * 0.1031); p3 += dot(p3, p3.zyx + 31.32); return fract((p3.x + p3.y) * p3.z); }
float vnoise(vec3 p) {
  vec3 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  float a = hash13(i), b = hash13(i + vec3(1,0,0)), c = hash13(i + vec3(0,1,0)), d = hash13(i + vec3(1,1,0));
  float e = hash13(i + vec3(0,0,1)), g = hash13(i + vec3(1,0,1)), h = hash13(i + vec3(0,1,1)), k = hash13(i + vec3(1,1,1));
  return mix(mix(mix(a, b, f.x), mix(c, d, f.x), f.y), mix(mix(e, g, f.x), mix(h, k, f.x), f.y), f.z);
}
float fbm(vec3 p, int oct) {
  float sum = 0.0, wsum = 0.0, a = 0.5;
  for (int i = 0; i < 7; i++) { if (i >= oct) break; sum += a * vnoise(p); wsum += a; a *= 0.5; p = p * 2.03 + vec3(17.3, 5.1, 3.7); }
  return sum / wsum;
}
float bump(float x, float w) { return exp(-0.5 * x * x / (w * w)); }
void main() {
  // the direction of this texel, by the GL cube-map table (s, t from the face's own corner), in the galactic frame
  vec2 st = 2.0 * gl_FragCoord.xy / float(${ST_N}) - 1.0;
  vec3 dir;
  if (uFace == 0)      dir = vec3( 1.0, -st.y, -st.x);
  else if (uFace == 1) dir = vec3(-1.0, -st.y,  st.x);
  else if (uFace == 2) dir = vec3( st.x,  1.0,  st.y);
  else if (uFace == 3) dir = vec3( st.x, -1.0, -st.y);
  else if (uFace == 4) dir = vec3( st.x, -st.y,  1.0);
  else                 dir = vec3(-st.x, -st.y, -1.0);
  dir = normalize(dir);
  float lon = atan(dir.y, dir.x), lat = asin(clamp(dir.z, -1.0, 1.0));
  vec3 d = dir + vec3(uSeed);
  // domain warp: the lanes meander and tear
  vec3 w = vec3(fbm(d * 3.0 + 1.7, 4), fbm(d * 3.0 + 9.2, 4), fbm(d * 3.0 + 4.4, 4)) - 0.5;
  vec3 dw = d + uWarp * w;
  float core = bump(lon, 0.9);                                  // 1 toward the core, fading round the sky (about 50 degrees)
  // The rift: dust lies in the plane, thicker and wider toward the core, with a centre line that wanders.
  float b0 = 0.035 * sin(lon * 2.0 + 1.1) + 0.05 * (fbm(vec3(1.1 * cos(lon), 1.1 * sin(lon), uSeed), 4) - 0.5);   // periodic in longitude: no step at the wrap
  float h = radians(uRiftW) * (0.55 + 1.1 * core);
  float bb = lat - b0;
  float prof = bump(bb, h);
  float ridge = 1.0 - abs(2.0 * fbm(dw * 7.0, 5) - 1.0);        // thin filaments
  float clump = fbm(dw * 13.0 + 3.0, 5);                        // dense knots
  float fine = fbm(dw * uFineF, 4);                               // tatters at a degree and below
  float cond = smoothstep(0.34, 0.74, uRidgeW * ridge + (1.1 - uRidgeW) * clump);
  float rho = prof * cond * (1.0 + uFine * 2.0 * (fine - 0.5));
  float wisp = bump(bb, 3.0 * h) * smoothstep(0.55, 0.88, ridge) * (0.3 + fine) * 0.55;       // fainter strands off the plane
  float tau = uTau0 * (rho + wisp) * (0.35 + 0.65 * (0.4 + 0.6 * core));
  if (uPass < 0.5) {
    vec3 T = exp(-tau * vec3(0.95, 1.0, 1.06));
    // star clouds: patches of unresolved stars, 3 to 10 degrees, with grain on top; brighter and broader toward the core
    float p2 = bump(lat, radians(7.0) * (0.8 + 0.8 * core));
    float c1 = fbm(dw * 9.0 + 20.0, 5), c2 = fbm(dw * 30.0 + 40.0, 4);
    float S = exp(uCloudAmp * p2 * (1.6 * (c1 - 0.5) + 0.9 * (c2 - 0.5)));
    // The dust takes light out of the band, so the band is given some back, but only where there is dust to compensate for: this follows the
    // rift's own envelope (wide and strong toward the core) and is 1 away from it, so the faint sky beside the band is not lifted.
    float env = bump(bb, 1.3 * h) * (0.35 + 0.65 * (0.4 + 0.6 * core));
    gl_FragColor = vec4(T, S * (1.0 + (uGain - 1.0) * env));
  } else {
    // emission nebulae: rose patches (hydrogen red with the blue of the surrounding light) strung along the plane, strongest toward the core
    float n1 = smoothstep(0.52, 0.80, fbm(dw * 10.0 + 70.0, 5));
    float n2 = smoothstep(0.35, 0.80, fbm(dw * 36.0 + 90.0, 4));
    float p3 = bump(lat - 0.3 * b0, radians(2.2) * (0.7 + 0.7 * core)) * (0.35 + 0.65 * bump(lon, 1.3));
    float nb = n1 * n2 * p3;
    gl_FragColor = vec4(vec3(1.0, 0.27, 0.42) * nb, 1.0);
  }
}`;

async function inflate(b64: string): Promise<Uint8Array> {
  const bin = atob(b64);
  const raw = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) raw[i] = bin.charCodeAt(i);
  const stream = new Blob([raw]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export class GalaxyGlow {
  readonly mesh: THREE.Mesh;
  private geometry = new THREE.SphereGeometry(1, 48, 24);
  private material: THREE.ShaderMaterial;
  private frame = new THREE.Matrix3();
  private basis = new THREE.Matrix3();
  private texture: THREE.DataTexture | null = null;
  private loaded = false;
  private loadSeq = 0;
  /** Largest flux per steradian in the map (light-field units), 0 until it arrives. */
  peak = 0;
  /** Brightness against the stars; starts at GALAXY_GAIN and can be changed live. */
  gain = GALAXY_GAIN;

  constructor(skyRadius: number) {
    this.material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      side: THREE.BackSide,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.CustomBlending,
      blendEquation: THREE.AddEquation,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneMinusSrcColorFactor,
      uniforms: {
        uGlow:      { value: null },
        uFrame:     { value: this.frame },
        uBasis:     { value: this.basis },
        uScale:     { value: 1 },
        uSurround:  { value: new THREE.Vector3() },
        uMax:       { value: GALAXY_MAX },
        uSoft:      { value: GALAXY_SOFT },
        uSoftLod:   { value: GALAXY_SOFT_LOD },
        uTexel:     { value: new THREE.Vector2(1 / 512, 1 / 256) },
        uSurroundK: { value: GALAXY_SURROUND },
        uGainScale: { value: 0 },
        uScot:      { value: 0 },
        uScotMax:   { value: GALAXY_SCOT_MAX },
        uArmHue:    { value: lumNorm(GALAXY_ARM_HUE) },
        uCoreHue:   { value: lumNorm(GALAXY_CORE_HUE) },
        uHuePull:   { value: GALAXY_HUE_PULL },
        uCoreCos:   { value: new THREE.Vector2(GALAXY_CORE_COS_LO, GALAXY_CORE_COS_HI) },
        uRodW:      { value: new THREE.Vector3(...SCOTOPIC_ROD_W) },
        uRodTint:   { value: new THREE.Vector3(...SCOTOPIC_TINT_N) },
        uSrgbOut:   { value: 1 },
        uCloudT:    { value: null as THREE.Texture | null },
        uCloudOn:   { value: 0 },
        uHome: { value: new THREE.Vector4(0, 0, 0, 0) }, uOcc: { value: new THREE.Vector4(0, 0, 0, 0) }, ...bodyAirUniforms(),
        uS1: { value: null as THREE.Texture | null }, uS2: { value: null as THREE.Texture | null }, uStructOn: { value: 0 }, uNebLevel: { value: NEB_LEVEL }, uGrain: { value: GRAIN },
      },
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.scale.setScalar(skyRadius);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = -2;                       // after the sky dome and the moon, before the stars
    this.mesh.visible = false;
  }

  /** Decode a `glow` message. Async (the payload is zlib-compressed); a newer message wins. */
  async load(msg: GlowMessage): Promise<void> {
    const seq = ++this.loadSeq;
    const bytes = await inflate(msg.data);
    if (seq !== this.loadSeq) return;
    const w = msg.width, h = msg.height, n = w * h;
    if (bytes.byteLength < n * 6) return;
    const q = new Uint16Array(bytes.buffer, bytes.byteOffset, n * 3);
    const lo = msg.log2_min, span = msg.log2_max - msg.log2_min;
    const scale = Math.pow(2, msg.log2_max);              // the map is stored as value / scale, in 0..1
    this.peak = scale;
    const half = new Uint16Array(n * 4);
    const one = THREE.DataUtils.toHalfFloat(1);
    // Decoding log2 for three planes of w*h values: a table of the 65535 possible codes is cheaper.
    const lut = new Float32Array(65536);
    for (let v = 1; v < 65536; v++) lut[v] = Math.pow(2, lo + (v - 1) / 65534 * span - msg.log2_max);
    for (let i = 0; i < n; i++) {
      half[i * 4]     = THREE.DataUtils.toHalfFloat(lut[q[i]]);
      half[i * 4 + 1] = THREE.DataUtils.toHalfFloat(lut[q[n + i]]);
      half[i * 4 + 2] = THREE.DataUtils.toHalfFloat(lut[q[2 * n + i]]);
      half[i * 4 + 3] = one;
    }
    // The surround: the median glow per channel over the sphere, weighted by each row's solid angle.
    const med: number[] = [];
    for (let c = 0; c < 3; c++) {
      const idx: number[] = [];
      for (let r = 0; r < h; r++) { const wt = Math.max(1, Math.round(Math.cos(((r + 0.5) / h - 0.5) * Math.PI) * 8)); for (let x = 0; x < w; x += 4) for (let k = 0; k < wt; k++) idx.push(q[c * n + r * w + x]); }
      const arr = Uint16Array.from(idx).sort();
      med.push(lut[arr[arr.length >> 1]] * scale);
    }
    (this.material.uniforms.uSurround.value as THREE.Vector3).set(med[0], med[1], med[2]);
    const tex = new THREE.DataTexture(half, w, h, THREE.RGBAFormat, THREE.HalfFloatType);
    tex.minFilter = THREE.LinearFilter; tex.magFilter = THREE.LinearFilter;
    tex.wrapS = THREE.RepeatWrapping; tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.minFilter = THREE.LinearMipmapLinearFilter; tex.generateMipmaps = true; tex.needsUpdate = true;
    if (this.texture) this.texture.dispose();
    this.texture = tex;
    const u = this.material.uniforms;
    u.uGlow.value = tex;
    (u.uTexel.value as THREE.Vector2).set(1 / w, 1 / h);
    u.uScale.value = scale;
    const b = msg.basis;
    this.basis.set(b[0], b[1], b[2], b[3], b[4], b[5], b[6], b[7], b[8]);
    this.loaded = true;
  }


  private st1: THREE.WebGLCubeRenderTarget | null = null;
  private st2: THREE.WebGLCubeRenderTarget | null = null;
  /** Make the structure maps (once; or again after changing a parameter). */
  bakeStructure(renderer: THREE.WebGLRenderer, p: { tau0?: number; cloud?: number; riftW?: number; warp?: number; seed?: number; fine?: number; fineF?: number; ridgeW?: number; gain?: number } = {}) {
    const mk = () => new THREE.WebGLCubeRenderTarget(ST_N, { type: THREE.HalfFloatType, depthBuffer: false, generateMipmaps: true,
      minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter });
    if (!this.st1) this.st1 = mk(); if (!this.st2) this.st2 = mk();
    const mat = new THREE.ShaderMaterial({ vertexShader: ST_VERT, fragmentShader: ST_FRAG, depthTest: false, depthWrite: false,
      uniforms: { uPass: { value: 0 }, uFace: { value: 0 }, uGain: { value: p.gain ?? STRUCT_GAIN }, uTau0: { value: p.tau0 ?? STRUCT_TAU0 }, uCloudAmp: { value: p.cloud ?? STRUCT_CLOUD }, uSeed: { value: p.seed ?? STRUCT_SEED }, uRiftW: { value: p.riftW ?? STRUCT_RIFT_DEG }, uWarp: { value: p.warp ?? 0.12 }, uFine: { value: p.fine ?? 0.2 }, uFineF: { value: p.fineF ?? 24 }, uRidgeW: { value: p.ridgeW ?? 0.0 } } });
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
    const sc = new THREE.Scene(); const m = new THREE.Mesh(g, mat); m.frustumCulled = false; sc.add(m);
    const cam = new THREE.Camera(); const prev = renderer.getRenderTarget();
    for (let pass = 0; pass < 2; pass++) {
      const rt = pass === 0 ? this.st1 : this.st2;
      mat.uniforms.uPass.value = pass;
      for (let face = 0; face < 6; face++) { mat.uniforms.uFace.value = face; renderer.setRenderTarget(rt, face); renderer.render(sc, cam); }
    }
    renderer.setRenderTarget(prev);
    g.dispose(); mat.dispose();
    const u = this.material.uniforms;
    u.uS1.value = this.st1.texture; u.uS2.value = this.st2.texture;
  }
  /** Per frame. `view` null (an older server with no frame) hides the band. `gain` is the renderer's
   *  exposure gain with the cloud dimming applied, as given to the stars. */
  update(camera: THREE.PerspectiveCamera, view: GalaxyViewState | null, gain: number, srgbOut: boolean, renderer?: THREE.WebGLRenderer) {
    // Only in the dark: the Milky Way is a faint glow that twilight and moonlight wash out, so it fades in with the exposure gain.
    const t = Math.min(1, Math.max(0, Math.log(Math.max(gain, 1) / GALAXY_DARK_LO) / Math.log(GALAXY_DARK_HI / GALAXY_DARK_LO)));
    const dark = t * t * (3 - 2 * t);
    const visible = this.loaded && !!view && gain >= GALAXY_MIN_GAIN && dark > 0.002;
    this.mesh.visible = visible;
    if (!visible || !view) return;
    if (!this.st1 && renderer) { this.bakeStructure(renderer); this.material.uniforms.uStructOn.value = 1; }   // once, the first time the band is drawn
    const u = this.material.uniforms;
    this.mesh.position.copy(camera.position);               // camera-locked: the sky does not parallax
    const f = view.frame;
    this.frame.set(f[0], f[1], f[2], f[3], f[4], f[5], f[6], f[7], f[8]);
    u.uGainScale.value = SKY_EXPOSURE * this.gain * gain * Math.PI * dark;
    u.uScot.value = nightFactor(TONE_BASE_EXPOSURE * gain);
    u.uSrgbOut.value = srgbOut ? 1 : 0;
  }

  /** See StarField.setSpace: a world and one more sphere that hide the band. */
  setSpace(home: ArrayLike<number> | null, occ: ArrayLike<number> | null) {
    const u = this.material.uniforms;
    (u.uHome.value as THREE.Vector4).set(home ? home[0] : 0, home ? home[1] : 0, home ? home[2] : 0, home ? home[3] : 0);
    (u.uOcc.value as THREE.Vector4).set(occ ? occ[0] : 0, occ ? occ[1] : 0, occ ? occ[2] : 0, occ ? occ[3] : 0);
  }

  /** See StarField.setAir. */
  setAir(tex: THREE.Texture | null, elH: number) { setBodyAir(this.material.uniforms as unknown as BodyAirUniforms, tex, elH); }

  /** The sky's probe cube (Sky.probe.texture), or null before it has been drawn: hides the glow behind cloud. */
  setCloudProbe(tex: THREE.Texture | null) {
    this.material.uniforms.uCloudT.value = tex;
    this.material.uniforms.uCloudOn.value = tex ? 1 : 0;
  }

  private probeSrgb = 1;
  /** Draw state for the star cube the sea reflects (starProbe.ts): plain display values, no sRGB encode. Pair with endProbe(). */
  beginProbe() { const u = this.material.uniforms; this.probeSrgb = u.uSrgbOut.value; u.uSrgbOut.value = 0; }
  endProbe() { this.material.uniforms.uSrgbOut.value = this.probeSrgb; }

  dispose() {
    this.geometry.dispose();
    this.material.dispose();
    if (this.texture) this.texture.dispose();
    this.st1?.dispose(); this.st2?.dispose();
  }
}
